import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type CredMeta = { configured: boolean; masked: string; source?: 'db' | 'env' | 'none' };

type SettingsResponse = {
  ok?: boolean;
  settings: Record<string, any>;
  credentials: Record<string, CredMeta>;
};

const DEFAULTS = {
  soft_score_threshold: 60,
  min_buffer_atr_multiple: 2.5,
  max_portfolio_exposure: 50000,
  max_portfolio_exposure_btc: 2,
  weekend_buffer_multiplier: 1.3,
  fng_extreme_threshold: 25,
  fng_extreme_buffer_multiplier: 1.2,
  funding_extreme_threshold: 0.03,
  dvol_max_threshold: 80,
  macro_event_buffer_hours: 6,
  run_cron_morning: '14:00',
  run_cron_subscription: '14:50',
  settlement_window_utc: '07:30, 08:00',
  allowed_underlyings: 'BTCUSDT, ETHUSDT',
};

function unwrapJsonString(v: unknown, fallback: string): string {
  if (v == null) return fallback;
  if (typeof v === 'string') return v;
  return String(v);
}

function unwrapNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return fallback;
}

function formatList(v: unknown, fallback: string): string {
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'string') return v;
  return fallback;
}

export function SettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['bot-settings'],
    queryFn: () => api.get<SettingsResponse>('/api/bot/settings'),
  });

  const [softScore, setSoftScore] = useState(DEFAULTS.soft_score_threshold);
  const [minBufferAtr, setMinBufferAtr] = useState(DEFAULTS.min_buffer_atr_multiple);
  const [maxExposure, setMaxExposure] = useState(DEFAULTS.max_portfolio_exposure);
  const [maxExposureBtc, setMaxExposureBtc] = useState(DEFAULTS.max_portfolio_exposure_btc);
  const [weekendMult, setWeekendMult] = useState(DEFAULTS.weekend_buffer_multiplier);
  const [fngThreshold, setFngThreshold] = useState(DEFAULTS.fng_extreme_threshold);
  const [fngMult, setFngMult] = useState(DEFAULTS.fng_extreme_buffer_multiplier);
  const [fundingThreshold, setFundingThreshold] = useState(DEFAULTS.funding_extreme_threshold);
  const [dvolMax, setDvolMax] = useState(DEFAULTS.dvol_max_threshold);
  const [macroBuffer, setMacroBuffer] = useState(DEFAULTS.macro_event_buffer_hours);
  const [cronMorning, setCronMorning] = useState(DEFAULTS.run_cron_morning);
  const [cronSub, setCronSub] = useState(DEFAULTS.run_cron_subscription);
  const [settleWindow, setSettleWindow] = useState(DEFAULTS.settlement_window_utc);
  const [underlyings, setUnderlyings] = useState(DEFAULTS.allowed_underlyings);

  const [binanceKey, setBinanceKey] = useState('');
  const [binanceSecret, setBinanceSecret] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.settings) return;
    const s = data.settings;
    setSoftScore(unwrapNumber(s.soft_score_threshold, DEFAULTS.soft_score_threshold));
    setMinBufferAtr(unwrapNumber(s.min_buffer_atr_multiple, DEFAULTS.min_buffer_atr_multiple));
    setMaxExposure(unwrapNumber(s.max_portfolio_exposure, DEFAULTS.max_portfolio_exposure));
    setMaxExposureBtc(unwrapNumber(s.max_portfolio_exposure_btc, DEFAULTS.max_portfolio_exposure_btc));
    setWeekendMult(unwrapNumber(s.weekend_buffer_multiplier, DEFAULTS.weekend_buffer_multiplier));
    setFngThreshold(unwrapNumber(s.fng_extreme_threshold, DEFAULTS.fng_extreme_threshold));
    setFngMult(unwrapNumber(s.fng_extreme_buffer_multiplier, DEFAULTS.fng_extreme_buffer_multiplier));
    setFundingThreshold(unwrapNumber(s.funding_extreme_threshold, DEFAULTS.funding_extreme_threshold));
    setDvolMax(unwrapNumber(s.dvol_max_threshold, DEFAULTS.dvol_max_threshold));
    setMacroBuffer(unwrapNumber(s.macro_event_buffer_hours, DEFAULTS.macro_event_buffer_hours));
    setCronMorning(unwrapJsonString(s.run_cron_morning, DEFAULTS.run_cron_morning));
    setCronSub(unwrapJsonString(s.run_cron_subscription, DEFAULTS.run_cron_subscription));
    setSettleWindow(formatList(s.settlement_window_utc, DEFAULTS.settlement_window_utc));
    setUnderlyings(formatList(s.allowed_underlyings, DEFAULTS.allowed_underlyings));
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const settleParts = settleWindow
        .split(/[,;]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      const underParts = underlyings
        .split(/[,;]+/)
        .map((x) => x.trim().toUpperCase())
        .filter(Boolean);

      const credentials: Record<string, string> = {};
      if (binanceKey.trim()) credentials.binance_api_key = binanceKey.trim();
      if (binanceSecret.trim()) credentials.binance_api_secret = binanceSecret.trim();

      return api.put<SettingsResponse>('/api/bot/settings', {
        settings: {
          soft_score_threshold: softScore,
          min_buffer_atr_multiple: minBufferAtr,
          max_portfolio_exposure: maxExposure,
          max_portfolio_exposure_btc: maxExposureBtc,
          weekend_buffer_multiplier: weekendMult,
          fng_extreme_threshold: fngThreshold,
          fng_extreme_buffer_multiplier: fngMult,
          funding_extreme_threshold: fundingThreshold,
          dvol_max_threshold: dvolMax,
          macro_event_buffer_hours: macroBuffer,
          run_cron_morning: cronMorning.trim(),
          run_cron_subscription: cronSub.trim(),
          settlement_window_utc: settleParts,
          allowed_underlyings: underParts,
        },
        credentials,
      });
    },
    onSuccess: () => {
      setFormError(null);
      setFormOk('Pengaturan tersimpan.');
      setBinanceKey('');
      setBinanceSecret('');
      qc.invalidateQueries({ queryKey: ['bot-settings'] });
      setTimeout(() => setFormOk(null), 3000);
    },
    onError: (err: Error) => {
      setFormOk(null);
      setFormError(err.message || 'Gagal menyimpan');
    },
  });

  const creds = data?.credentials || {};

  const credPill = (meta?: CredMeta) => {
    if (!meta?.configured) {
      return <span className="settings-pill">belum di-set</span>;
    }
    const src = meta.source === 'env' ? 'env' : 'db';
    return (
      <span className="settings-pill settings-pill--ok">
        set ({src}) · {meta.masked}
      </span>
    );
  };

  return (
    <div className="gl-page settings-page">
      <div className="settings-page__header">
        <div>
          <div className="settings-page__title">Pengaturan Nevi</div>
          <div className="settings-page__sub">Indopoin · threshold Nevi, jadwal & kredensial API</div>
        </div>
      </div>

      {isLoading && <div className="gl-empty">Memuat pengaturan…</div>}
      {error && (
        <div className="settings-alert settings-alert--error">
          Gagal memuat: {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && (
        <form
          className="settings-form"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            setFormOk(null);
            save.mutate();
          }}
        >
          {/* ── Bot gates / scores ── */}
          <section className="g-card rvl settings-section">
            <div className="gl-label">Bot · gate & score</div>
            <div className="settings-grid">
              <label className="settings-field">
                <span className="settings-field__label">Soft score threshold</span>
                <input type="number" min={0} max={100} step={1} value={softScore}
                  onChange={(e) => setSoftScore(Number(e.target.value))} />
                <span className="settings-field__hint">Kandidat lolos soft score di bawah angka ini</span>
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Min buffer ATR multiple</span>
                <input type="number" min={0} step={0.1} value={minBufferAtr}
                  onChange={(e) => setMinBufferAtr(Number(e.target.value))} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Max portfolio exposure (USDT)</span>
                <input type="number" min={0} step={100} value={maxExposure}
                  onChange={(e) => setMaxExposure(Number(e.target.value))} />
                <span className="settings-field__hint">Batas maksimal USDT untuk PUT (Buy Low)</span>
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Max portfolio exposure (BTC)</span>
                <input type="number" min={0} step={0.1} value={maxExposureBtc}
                  onChange={(e) => setMaxExposureBtc(Number(e.target.value))} />
                <span className="settings-field__hint">Batas maksimal BTC untuk CALL (Sell High)</span>
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Weekend buffer multiplier</span>
                <input type="number" min={1} step={0.05} value={weekendMult}
                  onChange={(e) => setWeekendMult(Number(e.target.value))} />
              </label>
            </div>
          </section>

          {/* ── Market extremes ── */}
          <section className="g-card rvl rvl-1 settings-section">
            <div className="gl-label">Market extremes</div>
            <div className="settings-grid">
              <label className="settings-field">
                <span className="settings-field__label">Fear & Greed extreme threshold</span>
                <input type="number" min={0} max={100} step={1} value={fngThreshold}
                  onChange={(e) => setFngThreshold(Number(e.target.value))} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">F&G buffer multiplier</span>
                <input type="number" min={1} step={0.05} value={fngMult}
                  onChange={(e) => setFngMult(Number(e.target.value))} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Funding extreme threshold</span>
                <input type="number" min={0} step={0.001} value={fundingThreshold}
                  onChange={(e) => setFundingThreshold(Number(e.target.value))} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">DVOL max threshold</span>
                <input type="number" min={0} max={200} step={1} value={dvolMax}
                  onChange={(e) => setDvolMax(Number(e.target.value))} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Macro event buffer (jam)</span>
                <input type="number" min={0} max={168} step={1} value={macroBuffer}
                  onChange={(e) => setMacroBuffer(Number(e.target.value))} />
                <span className="settings-field__hint">Gate 1: buffer sebelum/sesudah event high-impact (default 6 jam)</span>
              </label>
            </div>
          </section>

          {/* ── Schedule ── */}
          <section className="g-card rvl rvl-2 settings-section">
            <div className="gl-label">Jadwal & underlying</div>
            <div className="settings-grid">
              <label className="settings-field">
                <span className="settings-field__label">Cron evaluasi (WIB)</span>
                <input type="text" value={cronMorning} placeholder="14:00"
                  onChange={(e) => setCronMorning(e.target.value)} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Cron subscription (WIB)</span>
                <input type="text" value={cronSub} placeholder="14:50"
                  onChange={(e) => setCronSub(e.target.value)} />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Settlement window UTC</span>
                <input type="text" value={settleWindow} placeholder="07:30, 08:00"
                  onChange={(e) => setSettleWindow(e.target.value)} />
                <span className="settings-field__hint">Pisahkan dengan koma</span>
              </label>
              <label className="settings-field">
                <span className="settings-field__label">Allowed underlyings</span>
                <input type="text" value={underlyings} placeholder="BTCUSDT, ETHUSDT"
                  onChange={(e) => setUnderlyings(e.target.value)} />
                <span className="settings-field__hint">Pisahkan dengan koma</span>
              </label>
            </div>
          </section>

          {/* ── Simpan (atas) ── */}
          <div className="settings-actions settings-actions--top">
            <button type="submit" className="glass-btn glass-btn--primary" disabled={save.isPending}>
              <span className="glass-btn-inner">
                {save.isPending ? 'Menyimpan…' : 'Simpan pengaturan'}
              </span>
            </button>
          </div>

          {/* ── Credentials ── */}
          <section className="g-card rvl rvl-3 settings-section">
            <div className="gl-label">API credentials</div>
            <p className="settings-section__note">
              Disimpan di <code>tc_bot.settings</code>. Runtime service: <strong>DB dulu</strong>, fallback env.
              Kosongkan field = biarkan nilai lama. Secret tidak ditampilkan plain.
              Ideal: key Binance bot terpisah (tanpa withdraw). Setelah simpan, aktif max ~15 detik.
            </p>
            <div className="settings-grid">
              <label className="settings-field">
                <span className="settings-field__label">
                  Binance API key
                  {credPill(creds.binance_api_key)}
                </span>
                <input
                  type="password"
                  autoComplete="off"
                  placeholder={creds.binance_api_key?.configured ? '•••• biarkan kosong untuk keep' : 'Masukkan API key'}
                  value={binanceKey}
                  onChange={(e) => setBinanceKey(e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-field__label">
                  Binance API secret
                  {credPill(creds.binance_api_secret)}
                </span>
                <input
                  type="password"
                  autoComplete="off"
                  placeholder={creds.binance_api_secret?.configured ? '•••• biarkan kosong untuk keep' : 'Masukkan API secret'}
                  value={binanceSecret}
                  onChange={(e) => setBinanceSecret(e.target.value)}
                />
              </label>
            </div>
          </section>

          {/* ── WA Notification Formats ── */}
          <section className="g-card rvl settings-section">
            <div className="gl-label">Notifikasi WA — DCD Position</div>
            <p className="settings-section__note">
              Dikirim otomatis ke <strong>089610000156</strong> via WA Gateway.{' '}
              ⏰ <strong>14:15 WIB</strong> (posisi) &amp; <strong>20:30 WIB</strong> (rekap bulan).
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
              {/* ── 14:15 Ada posisi ── */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gl-cyan)', marginBottom: 6 }}>
                  ⏰ 14:15 — Ada posisi aktif
                </div>
                <pre className="wa-format-preview">{`📊 POSISI DCD — 21 Jul 14:15
──────────────────────

Aktif: 3 | Modal: Rp 4.200.000 | Est profit: Rp 315.000

1. BTC PUT 85.000 — 200 USDT
   ⏳ Settle 26 Jul (5 hari) — APR 12,5%
   💰 Est: Rp 53.000

2. ETH CALL 3.500 — 0,5 ETH
   ⏳ Settle 28 Jul (7 hari) — APR 8,2%
   💰 Est: Rp 23.000

3. SOL PUT 150 — 100 USDT
   ⏳ Settle 30 Jul (9 hari) — APR 10,1%
   💰 Est: Rp 18.000

━━ LUNAS ━━
✓ BTC PUT 82.000 — 200 USDT kembali (APR 7,5%)`}</pre>
              </div>

              {/* ── 14:15 Semua settle ── */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gl-emerald)', marginBottom: 6 }}>
                  ⏰ 14:15 — Semua posisi sudah settle
                </div>
                <pre className="wa-format-preview">{`📊 POSISI DCD — 21 Jul 14:15
──────────────────────

✅ Semua posisi sudah settle.
Tidak ada posisi aktif saat ini.

Total modal bulan ini: Rp 4.200.000
Total profit bulan ini: Rp 315.000`}</pre>
              </div>

              {/* ── 14:15 Belum pernah subscribe ── */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gl-text-faint)', marginBottom: 6 }}>
                  ⏰ 14:15 — Belum ada posisi (nol)
                </div>
                <pre className="wa-format-preview">{`📊 POSISI DCD — 21 Jul 14:15
──────────────────────

📭 Belum ada posisi DCD sama sekali.
Bot siap di-run kapan saja.`}</pre>
              </div>

              {/* ── 20:30 Rekap ── */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gl-amber)', marginBottom: 6 }}>
                  ⏰ 20:30 — Rekap bulan (ada data)
                </div>
                <pre className="wa-format-preview">{`📊 REKAP JULI 2026 — 20:30
──────────────────────

💰 Total modal:      Rp 4.200.000
📈 Total profit:     Rp 315.000
🎯 Win rate:         80% (4 dari 5 settle profit)
📊 Rata-rata APR:    10,5%

PUT (Beli BTC di harga bawah):
  • 3 posisi | Modal: Rp 3.000.000 | Profit: Rp 215.000

CALL (Jual BTC di harga atas):
  • 2 posisi | Modal: 0,5 ETH (~Rp 750.000) | Profit: Rp 100.000`}</pre>
              </div>

              {/* ── 20:30 Kosong ── */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gl-text-faint)', marginBottom: 6 }}>
                  ⏰ 20:30 — Rekap bulan (belum ada data)
                </div>
                <pre className="wa-format-preview">{`📊 REKAP JULI 2026 — 20:30
──────────────────────

📭 Belum ada aktivitas DCD bulan ini.
Bot siap di-run kapan saja.`}</pre>
              </div>
            </div>
          </section>

          {(formError || formOk) && (
            <div className={`settings-alert ${formError ? 'settings-alert--error' : 'settings-alert--ok'}`}>
              {formError || formOk}
            </div>
          )}

          <div className="settings-actions">
            <button type="submit" className="glass-btn glass-btn--primary" disabled={save.isPending}>
              <span className="glass-btn-inner">
                {save.isPending ? 'Menyimpan…' : 'Simpan pengaturan'}
              </span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
